import { inject, Injectable } from '@angular/core';
import { FilterGroupModel, FilterTypeEnum, LayerDetailsModel, SpatialFilterGeometry, SpatialFilterModel } from '@tailormap-viewer/api';
import { nanoid } from 'nanoid';
import { combineLatest, concatMap, filter, forkJoin, map, Observable, of, switchMap, take } from 'rxjs';
import { Store } from '@ngrx/store';
import { selectViewerId } from '../../../state/core.selectors';
import { setSelectedFilterGroup, setSelectedLayers } from '../state/filter-component.actions';
import { selectSelectedFilterGroup, selectSelectedLayers } from '../state/filter-component.selectors';
import { FilterTypeHelper } from '../../../filter/helpers/filter-type.helper';
import { addFilterGroup, updateFilterGroup } from '../../../state/filter-state/filter.actions';
import { selectLayer } from '../../../map';
import { MapService } from '@tailormap-viewer/map';
import { DataSourceManagerService } from '../../../services';

@Injectable({
  providedIn: 'root',
})
export class SpatialFilterCrudService {

  private dataSourceManagerService = inject(DataSourceManagerService);
  private store$ = inject(Store);
  private mapService = inject(MapService);

  public updateSelectedLayers(layers: string[]) {
    this.store$.dispatch(setSelectedLayers({ layers }));
    this.updateSelectedGroup(group => this.updateLayers$(group, layers));
  }

  public addGeometry(geometry: SpatialFilterGeometry) {
    this.updateOrCreateGroup(
      [geometry],
      undefined,
      group => of({
        ...group,
        filters: group.filters.map(f => ({ ...f, geometries: [ ...f.geometries, geometry ] })),
      }),
    );
  }

  public updateGeometry(id: string, geometry: string) {
    this.updateSelectedGroup(group => of({
      ...group,
      filters: group.filters.map(f => ({ ...f, geometries: f.geometries.map(g => g.id === id ? { ...g, geometry } : g) })),
    }));
  }

  public removeGeometry(id: string) {
    this.updateSelectedGroup(group => of({
      ...group,
      filters: group.filters.map(f => ({ ...f, geometries: f.geometries.filter(g => g.id !== id) })),
    }));
  }

  public updateBuffer(buffer: number | undefined) {
    this.updateSelectedGroup(group => of({
      ...group,
      filters: group.filters.map(f => ({ ...f, buffer })),
    }));
  }

  public updateReferenceLayer(layer: string | undefined) {
    this.updateOrCreateGroup(
      [],
      layer,
      group => of({
        ...group,
        error: undefined,
        filters: group.filters.map(f => {
          const currentReferenceLayer = f.baseLayerId;
          const geometries = f.geometries.filter(g => {
            if (typeof layer === 'undefined') {
              return typeof g.referenceLayerId === 'undefined';
            }
            if (layer !== currentReferenceLayer) {
              return typeof g.referenceLayerId === 'undefined' || g.referenceLayerId !== currentReferenceLayer;
            }
            return true;
          });
          return {
            ...f,
            geometries,
            baseLayerId: layer,
            exceededMaxFeatures: undefined,
          };
        }),
      }),
    );
    return;
  }

  private updateLayers$(
    group: FilterGroupModel<SpatialFilterModel>,
    layers: string[],
  ): Observable<FilterGroupModel> {
    return this.getLayerDetails$(layers).pipe(
      map(layerDetails => {
        if (layerDetails.length === 0) {
          return group;
        }
        return {
          ...group,
          layerIds: layerDetails.map(layer => layer.id),
          filters: group.filters.map(f => {
            return {
              ...f,
              geometryColumns: layerDetails.map(layer => ({
                layerId: layer.id,
                column: [layer.geometryAttribute],
              })),
            };
          }),
        };
      }),
    );
  }

  private getSelectedGroup$(): Observable<FilterGroupModel<SpatialFilterModel>> {
    return this.store$.select(selectSelectedFilterGroup)
      .pipe(
        take(1),
        filter((group): group is FilterGroupModel<SpatialFilterModel> => !!group && FilterTypeHelper.isSpatialFilterGroup(group)),
      );
  }

  private updateOrCreateGroup(
    geometry: SpatialFilterGeometry[],
    referenceLayer: string | undefined,
    updateFn: (group: FilterGroupModel<SpatialFilterModel>) => Observable<FilterGroupModel>,
  ) {
    combineLatest([
      this.store$.select(selectSelectedFilterGroup),
      this.store$.select(selectSelectedLayers),
    ])
      .pipe(
        take(1),
      )
      .subscribe(([ selectedGroup, selectedLayers ]) => {
        if (selectedGroup && !FilterTypeHelper.isSpatialFilterGroup(selectedGroup)) {
          return;
        }
        if (!selectedGroup) {
          this.createSpatialFilterGroup(geometry, selectedLayers, referenceLayer);
          return;
        }
        this.updateSelectedGroup(updateFn);
      });
  }

  private updateSelectedGroup(updateFn: (group: FilterGroupModel<SpatialFilterModel>) => Observable<FilterGroupModel>) {
    this.getSelectedGroup$()
      .pipe(concatMap(group => updateFn(group)))
      .subscribe(group => this.store$.dispatch(updateFilterGroup({ filterGroup: group })));
  }

  private createSpatialFilterGroup(
    geometries: SpatialFilterGeometry[],
    layers: string[],
    referenceLayer?: string,
  ): void {
    this.getLayerDetails$(layers).pipe(
      switchMap(layerDetails => {
        if (layerDetails.length === 0) {
          return of(null);
        }
        return this.mapService.getProjectionCode$().pipe(
          take(1),
          map(projectionCode =>
            this.createFilterForLayers(layerDetails, geometries, projectionCode, referenceLayer),
          ),
        );
      }),
    ).subscribe(filterGroup => {
      if (filterGroup === null) {
        return;
      }
      this.store$.dispatch(addFilterGroup({ filterGroup }));
      this.store$.dispatch(setSelectedFilterGroup({ filterGroup }));
    });
  }

  private getLayerDetails$(
    layers: string[],
  ): Observable<LayerDetailsModel[]> {
    if (layers.length === 0) {
      return of([]);
    }
    return this.store$.select(selectViewerId).pipe(
      concatMap(applicationId => {
        if (!applicationId) {
          return of([]);
        }
        return forkJoin(layers.map(layer => this.store$.select(selectLayer(layer)).pipe(
          take(1),
          switchMap(lyr => lyr ? this.dataSourceManagerService.getDescribeLayer$({
            layerId: layer,
            layerName: lyr.layerName,
            applicationId: applicationId,
          }) : of(null))),
        ));
      }),
      map(results => results.filter((result): result is LayerDetailsModel => !!result)),
      take(1),
    );
  }

  private createFilterForLayers(
    layers: LayerDetailsModel[],
    geometries: SpatialFilterGeometry[],
    projectionCode: string,
    referenceLayer?: string,
  ): FilterGroupModel<SpatialFilterModel> {
    const spatialFilter: SpatialFilterModel = {
      id: nanoid(6),
      type: FilterTypeEnum.SPATIAL,
      geometries,
      baseLayerId: referenceLayer,
      projectionCode,
      buffer: undefined,
      geometryColumns: layers.map(layer => ({
        layerId: layer.id,
        column: [layer.geometryAttribute],
      })),
    };
    return {
      id: nanoid(6),
      type: FilterTypeEnum.SPATIAL,
      layerIds: layers.map(layer => layer.id),
      operator: 'AND',
      filters: [spatialFilter],
      source: 'SPATIAL_FILTER_FORM',
    };
  }
}
